/**
 * Clock for painted-frame dwell (`content_not_advancing` / `lastFrameTransitionAt`).
 * Independent of the timers-overlay `visibleFrameStartMs`, which resets when the overlay opens
 * and when `currentFrame` flickers to null while a held surface still paints.
 */

export interface IPaintedVisibleFrameStamp {
  lastPaintedFrameId: string | null
  paintedChangedAtMs: number
}

/**
 * Wall gap between consecutive 1s dwell polls that can only mean the app stopped running
 * (Android doze, display off, WebView backgrounded). Well above GC / jank pauses.
 */
export const PAINTED_DWELL_SUSPEND_GAP_MS = 5_000

/**
 * Painted dwell must measure time the creative was on screen **while the player was running**, not
 * raw wall time. A suspended player paints nothing and stamps nothing, so on resume the raw dwell
 * equals the whole sleep and `content_not_advancing` fires on the first poll — before the wake
 * resync can repaint. Recovery then runs against a screen that was never stuck (#1500: vehicle
 * screens suspend for minutes, wake, and wedge on the recovery bootstrap).
 *
 * Rebasing costs nothing for a screen that really is frozen: it re-arms one full stall budget later.
 */
export function rebasePaintedVisibleFrameDwellOnResume(input: {
  paintedChangedAtMs: number
  lastPollWallMs: number
  nowMs: number
}): number {
  if (input.paintedChangedAtMs <= 0 || input.lastPollWallMs <= 0) {
    return input.paintedChangedAtMs
  }
  if (input.nowMs - input.lastPollWallMs < PAINTED_DWELL_SUSPEND_GAP_MS) {
    return input.paintedChangedAtMs
  }
  return input.nowMs
}


/**
 * Timer-stuck clocks (`visibleFrameStartMs` / `lastTimingChangeMs`) also use wall time.
 * After Android `process_suspend`, raw dwell + flatline include sleep and false-positive
 * `playbackTimerStuck` → Studio PLAYBACK_STALLED + recovery reload loops (#1969).
 * Same suspend-gap threshold as painted dwell (#1500).
 */
export function rebasePlaybackTimerStuckClocksOnResume(input: {
  visibleFrameStartMs: number
  lastTimingChangeMs: number | null
  lastPollWallMs: number
  nowMs: number
  /** Defaults to {@link PAINTED_DWELL_SUSPEND_GAP_MS}. */
  suspendGapMs?: number
}): {
  visibleFrameStartMs: number
  lastTimingChangeMs: number | null
  didRebase: boolean
} {
  const gapMs = input.suspendGapMs ?? PAINTED_DWELL_SUSPEND_GAP_MS
  if (
    input.visibleFrameStartMs <= 0
    || input.lastPollWallMs <= 0
    || input.nowMs - input.lastPollWallMs < gapMs
  ) {
    return {
      visibleFrameStartMs: input.visibleFrameStartMs,
      lastTimingChangeMs: input.lastTimingChangeMs,
      didRebase: false
    }
  }
  return {
    visibleFrameStartMs: input.nowMs,
    lastTimingChangeMs: input.nowMs,
    didRebase: true
  }
}

export function stampPaintedVisibleFrameChange(input: {
  paintedFrameId: string | null
  lastPaintedFrameId: string | null
  paintedChangedAtMs: number
  nowMs: number
}): IPaintedVisibleFrameStamp {
  if (input.paintedFrameId === input.lastPaintedFrameId) {
    return {
      lastPaintedFrameId: input.lastPaintedFrameId,
      paintedChangedAtMs: input.paintedChangedAtMs
    }
  }
  // Empty surface: keep prior stamp so a brief null flicker does not understate dwell of the
  // creative that was (and may still be) on screen via a held render surface.
  if (input.paintedFrameId == null || input.paintedFrameId === '') {
    return {
      lastPaintedFrameId: null,
      paintedChangedAtMs: input.paintedChangedAtMs
    }
  }
  return {
    lastPaintedFrameId: input.paintedFrameId,
    paintedChangedAtMs: input.nowMs
  }
}
